import { useEffect, useRef, useState } from "react";

const reducedMotion = () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Counts from 0 to `value` over ~600 ms the first time a number appears.
 * Later changes jump straight to the new value: a live counter that wobbles
 * on every poll would look like it is still thinking.
 */
export function useCountUp(value: number, ms = 600): number {
  const [shown, setShown] = useState(() => (reducedMotion() ? value : 0));
  const done = useRef(false);

  useEffect(() => {
    if (done.current || reducedMotion() || value === 0) {
      done.current = true;
      setShown(value);
      return;
    }
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / ms);
      const eased = 1 - Math.pow(1 - t, 3);
      setShown(Math.round(value * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
      else done.current = true;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, ms]);

  return shown;
}
